import {Button, DatePicker, Input, Select} from 'antd';
import {useCallback, useState} from 'react';

import {Locale} from '../../../provider/locale/locale';
import {useLocale} from '../../../provider/locale/locale-hook';
import {LangKeyType} from '../../../provider/locale/translation/type';
import {useSnackbar} from '../../../provider/snackbar/snackbar-hook';
import {deleteEventImage} from '../../../service/events/events-api';
import {useUploadEventImage} from '../../../service/events/events-hook';
import {
    CommonEventStatusEnum,
    EVENT_AGE_LIST,
    EVENT_TYPE_LIST,
    EventAgeEnum,
    EventFormFieldsEnum,
    EventFormType,
    EventTypeEnum,
} from '../../../service/events/events-type';
import {
    PostSelectorCompanyType,
    UseCompaniesSelectorHookType,
} from '../../../service/feature-companies/feature-companies-type';
import {useFormRules} from '../../../service/form/form-rules-hook';
import {Form} from '../../../typings/antd';
import {noop} from '../../../util/function';
import {PublicationBrandOrCompaniesField} from '../../publication-wizard/publication-brand-or-companies-field/publication-brand-or-companies-field';
import {
    PublicationCompaniesSourceEnum,
    selectBrandCompanies,
} from '../../publication-wizard/publication-brand-or-companies-field/publication-brand-or-companies-helper';
import {PublicationFieldLabel} from '../../publication-wizard/publication-field-label';
import {PublicationFooterButtonTooltip} from '../../publication-wizard/publication-footer-button-tooltip';
import * as styles from '../../publication-wizard/publication-form-layout.scss';
import {PublicationMediaUploader} from '../../publication-wizard/publication-media-uploader';
import {PublicationTitleInput} from '../../publication-wizard/publication-title-input';
import {Text} from '../../text/text';
import {getEventAgeLabel, getEventTypeLangKey} from '../events-helper';

import {isEventFormFilled, selectEventFormFilledState} from './event-form-helper';

// Yandex help caps the event description at 3000 characters; the title has no documented
// limit, so we apply a moderate 100-char cap to keep titles readable.
const EVENT_TITLE_MAX_LENGTH = 100;
const EVENT_DESCRIPTION_MAX_LENGTH = 3000;

// Yandex help for events: JPG/PNG, up to 4 images, 320 × 160 … 5000 × 5000 px, recommended 400 × 160.
const EVENT_MAX_IMAGES = 4;
const EVENT_MEDIA_MIN_WIDTH = 320;
const EVENT_MEDIA_MIN_HEIGHT = 160;
const EVENT_MEDIA_MAX_WIDTH = 5000;
const EVENT_MEDIA_MAX_HEIGHT = 5000;
const EVENT_MEDIA_RECOMMENDED_WIDTH = 400;
const EVENT_MEDIA_RECOMMENDED_HEIGHT = 160;

const EVENT_MEDIA_RESOLUTION_VALUE_MAP = {
    maxHeight: EVENT_MEDIA_MAX_HEIGHT,
    maxWidth: EVENT_MEDIA_MAX_WIDTH,
    minHeight: EVENT_MEDIA_MIN_HEIGHT,
    minWidth: EVENT_MEDIA_MIN_WIDTH,
    recommendedHeight: EVENT_MEDIA_RECOMMENDED_HEIGHT,
    recommendedWidth: EVENT_MEDIA_RECOMMENDED_WIDTH,
};

const mediaBanner = (
    <>
        <Text block bolder stringKey="EVENTS__FORM__UPLOAD_IMAGES__COUNT" />
        <Text block secondary small stringKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__RULES_2" />
        <Text
            block
            secondary
            small
            stringKey="EVENTS__FORM__UPLOAD_IMAGES__RESOLUTION"
            valueMap={EVENT_MEDIA_RESOLUTION_VALUE_MAP}
        />
    </>
);

const mediaTooltip = (
    <>
        <Text block inheritColor stringKey="EVENTS__FORM__UPLOAD_IMAGES__COUNT__TOOLTIP" />
        <Text block inheritColor stringKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__RULES_2" />
        <Text
            block
            inheritColor
            stringKey="EVENTS__FORM__UPLOAD_IMAGES__RESOLUTION"
            valueMap={EVENT_MEDIA_RESOLUTION_VALUE_MAP}
        />
    </>
);

type SubmittingActionType = 'draft' | 'submit' | null;

type PropsType = {
    onSubmit: (values: EventFormType, status: CommonEventStatusEnum) => Promise<void>;
    // Closes the modal straight away, used once the event has been saved.
    closeModal: () => void;
    // Asks to confirm discarding the changes first, shared with the modal's own close control.
    onCancel: () => void;
    mode: 'edit' | 'create';
    companiesSelector: UseCompaniesSelectorHookType<PostSelectorCompanyType>;
    initialValues?: Partial<EventFormType>;
};

// Single-step event form: companies/brand, the event details and its images in one modal.
export function EventForm(props: PropsType): JSX.Element {
    const {onSubmit, closeModal, onCancel, mode, companiesSelector, initialValues} = props;

    const [form] = Form.useForm<EventFormType>();
    const {getLocalizedString} = useLocale();
    const {snackbar} = useSnackbar();
    const {requiredFieldRule, requiredDateRangeFieldRule, maxLengthFieldRule} = useFormRules();
    const {mutateAsync: uploadImage} = useUploadEventImage();

    // An existing event is only known by its companies, so editing always starts on the companies tab.
    const [companiesSource, setCompaniesSource] = useState<PublicationCompaniesSourceEnum>(
        mode === 'edit' ? PublicationCompaniesSourceEnum.Companies : PublicationCompaniesSourceEnum.Brand
    );
    const [submittingAction, setSubmittingAction] = useState<SubmittingActionType>(null);
    const [isMediaBlocked, setIsMediaBlocked] = useState<boolean>(false);

    const filledState = Form.useWatch(selectEventFormFilledState, {form, preserve: true});

    const reportSubmitError = useCallback(() => {
        snackbar.error({
            message: getLocalizedString(
                mode === 'create' ? 'EVENTS__SNACKBAR__CREATE_ERROR' : 'EVENTS__SNACKBAR__UPDATE_ERROR'
            ),
            description: getLocalizedString('EVENTS__SNACKBAR__ERROR__DESCRIPTION'),
        });
    }, [getLocalizedString, mode, snackbar]);

    const handleSubmit = useCallback(
        async (status: CommonEventStatusEnum) => {
            setSubmittingAction(status === CommonEventStatusEnum.Draft ? 'draft' : 'submit');

            // Field errors are rendered inline by the form itself, so a rejection is silent here.
            const isValid = await form
                .validateFields()
                .then(() => true)
                .catch(() => false);

            const formValues = form.getFieldsValue(true);
            const brandId = companiesSource === PublicationCompaniesSourceEnum.Brand ? formValues.brandId : null;

            // Expanding a brand into its companies is a request of its own. If it fails the mutation
            // never runs, so nothing else would tell the user the event was not saved.
            const isSelectionApplied =
                isValid && brandId
                    ? await selectBrandCompanies(companiesSelector, brandId)
                          .then(() => true)
                          .catch(() => {
                              reportSubmitError();

                              return false;
                          })
                    : isValid;

            // The mutation reports its own request errors through a snackbar.
            const isSubmitted = isSelectionApplied
                ? await onSubmit(formValues, status)
                      .then(() => true)
                      .catch(() => false)
                : false;

            setSubmittingAction(null);

            if (isSubmitted) {
                closeModal();
            }
        },
        [closeModal, companiesSelector, companiesSource, form, onSubmit, reportSubmitError]
    );

    const isSubmitDisabled = isMediaBlocked || !isEventFormFilled(filledState, companiesSource);
    const isBusy = submittingAction !== null;

    // While an upload is in flight (or has failed) there is nothing left for the user to fill in, so
    // the generic "fill in the required fields" hint would send them looking for a field that is fine.
    const disabledTooltipLangKey: LangKeyType = isMediaBlocked
        ? 'POSTS_FORM__MEDIA_IN_PROGRESS_ERROR'
        : 'PUBLICATION_FORM__FILL_REQUIRED_FIELDS';

    return (
        <Form<EventFormType> form={form} initialValues={initialValues} layout="vertical">
            <PublicationBrandOrCompaniesField
                companiesSelector={companiesSelector}
                disabled={isBusy}
                onSourceChange={setCompaniesSource}
                source={companiesSource}
            />

            <Form.Item<EventFormType>
                label={<Locale stringKey="EVENTS__FORM__TITLE" />}
                name={EventFormFieldsEnum.Title}
                rules={[requiredFieldRule, maxLengthFieldRule(EVENT_TITLE_MAX_LENGTH)]}
            >
                <PublicationTitleInput
                    maxLength={EVENT_TITLE_MAX_LENGTH}
                    placeholder={getLocalizedString('EVENTS__FORM__TITLE__PLACEHOLDER')}
                />
            </Form.Item>

            <Form.Item<EventFormType>
                label={<Locale stringKey="EVENTS__FORM__DESCRIPTION" />}
                name={EventFormFieldsEnum.Description}
                rules={[requiredFieldRule, maxLengthFieldRule(EVENT_DESCRIPTION_MAX_LENGTH)]}
            >
                <Input.TextArea
                    autoSize={{minRows: 3, maxRows: 8}}
                    maxLength={EVENT_DESCRIPTION_MAX_LENGTH}
                    placeholder={getLocalizedString('EVENTS__FORM__DESCRIPTION__PLACEHOLDER')}
                    showCount
                />
            </Form.Item>

            <div className={styles.PublicationFormLayout_row}>
                <Form.Item<EventFormType>
                    className={styles.PublicationFormLayout_rowItem}
                    label={<Locale stringKey="EVENTS__FORM__TYPE" />}
                    name={EventFormFieldsEnum.EventType}
                    rules={[requiredFieldRule]}
                >
                    <Select<EventTypeEnum>
                        optionFilterProp="label"
                        options={EVENT_TYPE_LIST.map((eventType) => ({
                            label: getLocalizedString(getEventTypeLangKey(eventType)),
                            value: eventType,
                        }))}
                        placeholder={getLocalizedString('EVENTS__FORM__TYPE__PLACEHOLDER')}
                    />
                </Form.Item>

                {/* Optional, exactly like in Yandex Business. */}
                <Form.Item<EventFormType>
                    className={styles.PublicationFormLayout_rowItem}
                    label={<Locale stringKey="EVENTS__FORM__AGE" />}
                    name={EventFormFieldsEnum.MinAge}
                >
                    <Select<EventAgeEnum>
                        options={EVENT_AGE_LIST.map((age) => ({label: getEventAgeLabel(age), value: age}))}
                        placeholder={getLocalizedString('EVENTS__FORM__AGE__PLACEHOLDER')}
                    />
                </Form.Item>
            </div>

            <Form.Item<EventFormType>
                label={<Locale stringKey="EVENTS__FORM__PERIOD" />}
                name={EventFormFieldsEnum.Period}
                rules={[requiredDateRangeFieldRule()]}
            >
                <DatePicker.RangePicker
                    className={styles.PublicationFormLayout_fullWidthControl}
                    placeholder={[
                        getLocalizedString('EVENTS__FORM__START_AT'),
                        getLocalizedString('EVENTS__FORM__FINISH_AT'),
                    ]}
                    showTime
                />
            </Form.Item>

            <Form.Item<EventFormType>
                label={
                    <PublicationFieldLabel
                        iconPosition="after"
                        labelLangKey="EVENTS__FORM__IMAGES"
                        tooltip={mediaTooltip}
                    />
                }
                name={EventFormFieldsEnum.Images}
                rules={[requiredFieldRule]}
            >
                <PublicationMediaUploader
                    banner={mediaBanner}
                    layout="tiles"
                    maxCount={EVENT_MAX_IMAGES}
                    maxHeight={EVENT_MEDIA_MAX_HEIGHT}
                    maxWidth={EVENT_MEDIA_MAX_WIDTH}
                    minHeight={EVENT_MEDIA_MIN_HEIGHT}
                    minWidth={EVENT_MEDIA_MIN_WIDTH}
                    onBlockedChange={setIsMediaBlocked}
                    remove={(imageId) => deleteEventImage(imageId).catch(noop)}
                    upload={(file) => uploadImage(file)}
                    uploaderTextLangKey="EVENTS__FORM__UPLOAD"
                />
            </Form.Item>

            <div className={styles.PublicationFormLayout_footer}>
                <Button disabled={isBusy} onClick={onCancel}>
                    <Locale stringKey="BUTTON__CANCEL" />
                </Button>

                {mode === 'create' && (
                    <PublicationFooterButtonTooltip
                        isDisabled={isMediaBlocked}
                        langKey="POSTS_FORM__MEDIA_IN_PROGRESS_ERROR"
                    >
                        <Button
                            // Saving mid-upload would store the draft without the image that is still on
                            // its way, leaving the uploaded file orphaned on the server.
                            disabled={isBusy || isMediaBlocked}
                            loading={submittingAction === 'draft'}
                            onClick={() => handleSubmit(CommonEventStatusEnum.Draft)}
                        >
                            <Locale stringKey="POSTS_FORM__BUTTON__SAVE_DRAFT" />
                        </Button>
                    </PublicationFooterButtonTooltip>
                )}

                <PublicationFooterButtonTooltip isDisabled={isSubmitDisabled} langKey={disabledTooltipLangKey}>
                    <Button
                        disabled={isBusy || isSubmitDisabled}
                        loading={submittingAction === 'submit'}
                        onClick={() => handleSubmit(CommonEventStatusEnum.Sent)}
                        type="primary"
                    >
                        <Locale stringKey={mode === 'create' ? 'BUTTON__CREATE' : 'EVENTS__FORM__SAVE'} />
                    </Button>
                </PublicationFooterButtonTooltip>
            </div>
        </Form>
    );
}
